import { useEffect, useRef } from 'react';
import styles from './ConfettiCanvas.module.css';

const PARTICLE_COUNT = 80;
const GRAVITY = 0.15;
const COLORS = [
  '#ff6821', // brand-orange
  '#e8634f', // celebration-orange
  '#2ab8a8', // teal
  '#F5A623', // qi amber
  '#7B5EA7', // ming purple
  '#5BC8F5', // feng sky blue
  '#4CAF7D', // yong green
];

function createParticle(canvasWidth, canvasHeight) {
  return {
    x: canvasWidth / 2 + (Math.random() - 0.5) * canvasWidth * 0.6,
    y: canvasHeight * 0.3 + (Math.random() - 0.5) * 100,
    vx: (Math.random() - 0.5) * 8,
    vy: -(Math.random() * 6 + 2),
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    size: Math.random() * 6 + 3,
    rotation: Math.random() * 360,
    rotationSpeed: (Math.random() - 0.5) * 10,
    opacity: 1,
    shape: Math.random() > 0.5 ? 'rect' : 'circle',
  };
}

export default function ConfettiCanvas({ duration = 3000 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const particles = Array.from({ length: PARTICLE_COUNT }, () =>
      createParticle(rect.width, rect.height)
    );

    let animFrame;
    const startTime = performance.now();

    function animate(now) {
      const elapsed = now - startTime;
      const fadeStart = duration * 0.7;

      ctx.clearRect(0, 0, rect.width, rect.height);

      for (const p of particles) {
        p.x += p.vx;
        p.vy += GRAVITY;
        p.y += p.vy;
        p.rotation += p.rotationSpeed;

        if (elapsed > fadeStart) {
          p.opacity = Math.max(0, 1 - (elapsed - fadeStart) / (duration - fadeStart));
        }

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.globalAlpha = p.opacity;
        ctx.fillStyle = p.color;

        if (p.shape === 'rect') {
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.restore();
      }

      if (elapsed < duration) {
        animFrame = requestAnimationFrame(animate);
      }
    }

    animFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animFrame);
  }, [duration]);

  return (
    <canvas
      ref={canvasRef}
      className={styles.canvas}
    />
  );
}
